export interface DashboardRequestScope {
  userId: string;
  gymId: string;
  startDate: string;
  endDate: string;
}

export const dashboardScopeKey = (scope: DashboardRequestScope) =>
  `${scope.userId}:${scope.gymId}:${scope.startDate}:${scope.endDate}`;

export const dashboardRangeFromScope = (scope: string | null) => {
  const parts = scope?.split(':');
  const startDate = parts?.at(-2);
  const endDate = parts?.at(-1);
  return startDate && endDate ? { startDate, endDate } : null;
};

const inflight = new Map<string, Promise<unknown>>();
const latestTokens = new Map<string, number>();

export function runDashboardRequest<T>(
  resource: string,
  scope: DashboardRequestScope,
  request: () => Promise<T>,
  variant = "",
): { token: number; promise: Promise<T>; key: string } {
  const key = `${resource}:${dashboardScopeKey(scope)}:${variant}`;
  const existing = inflight.get(key) as Promise<T> | undefined;
  const token = (latestTokens.get(resource) ?? 0) + 1;
  latestTokens.set(resource, token);

  if (existing) return { token, promise: existing, key };

  const promise = request().finally(() => {
    if (inflight.get(key) === promise) inflight.delete(key);
  });
  inflight.set(key, promise);
  return { token, promise, key };
}

export const isLatestDashboardRequest = (resource: string, token: number) =>
  latestTokens.get(resource) === token;

export const clearDashboardRequestCoordinator = () => {
  inflight.clear();
  // Keep generations monotonic: clearing them could validate an old response
  // when a new request reuses token 1 after a tenant reset.
  for (const [resource, token] of latestTokens) latestTokens.set(resource, token + 1);
};